import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import axios, { CancelToken } from "axios";

import { GetPowerCarts, IPowerCart } from "api/rpc/2024-04/facilityAdmin/facility/powercart/powercart";
import { PutBookingParticipant } from "api/rpc/2024-04/facilityAdmin/teesheet/booking";
import { StatusCode } from "api/protocols";

import { showError } from "redux/actions/ui";
import { useAppDispatch, useAppSelector } from "hooks/redux";

import Sheet from "components/sheet/Sheet";
import FormLayout from "components/form/FormLayout";
import Input from "components/form/input/Input";
import SelectNew from "components/select/SelectNew";
import Portal from "elements/Portal";

// Should be 2 for most courses but we can increase if needed, maybe make this a setting?
export const MAX_SEATS_PER_CART = 2;

export interface IPowerCartNumbersParticipant {
  participant_id: number;
  full_name: string;
  guest?: boolean;
  customer_type?: string;
  cart_customer_type?: string;
  power_cart_id?: number | null;
  power_cart_number?: number | null;
  seat_number?: number | null;
}

interface ISelectedPowerCart {
  power_cart_id: number | null;
  power_cart_number: number | null;
  participant_id: number;
  seat_number: number | null;
}

interface IPowerCartNumbersModalProps {
  open: boolean;
  participants: IPowerCartNumbersParticipant[];
  onCancel: () => void;
  onSaveSuccess?: () => void;
}

export default function PowerCartNumbersModal(props: IPowerCartNumbersModalProps) {
  const { open, participants, onCancel, onSaveSuccess } = props;
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const { facilityStore } = useAppSelector(store => store);

  const [selectedPowerCarts, setSelectedPowerCarts] = useState<ISelectedPowerCart[]>([]);
  const [availablePowerCarts, setAvailablePowerCarts] = useState<IPowerCart[]>([]);
  const [loadedAvailableCarts, setLoadedAvailableCarts] = useState(false);
  const participantsRef = useRef(participants);
  participantsRef.current = participants;

  useEffect(() => {
    if (!open) {
      return;
    }

    const source = axios.CancelToken.source();
    const currentParticipants = participantsRef.current;

    setSelectedPowerCarts(
      currentParticipants.map(participant => ({
        power_cart_id: participant.power_cart_id ?? null,
        participant_id: participant.participant_id,
        seat_number: participant.seat_number ?? null,
        power_cart_number: participant.power_cart_number ?? null,
      })),
    );

    if (facilityStore.teeSheetSettings?.use_cart_inventory) {
      void loadPowerCartList(source.token, currentParticipants);
    } else {
      setLoadedAvailableCarts(true);
    }

    return () => source.cancel();
  }, [open, facilityStore.teeSheetSettings?.use_cart_inventory]);

  async function loadPowerCartList(source: CancelToken, currentParticipants: IPowerCartNumbersParticipant[]) {
    setLoadedAvailableCarts(false);
    const powerCartRes = await GetPowerCarts({}, false, source);

    if (powerCartRes.status !== StatusCode.OK) {
      setAvailablePowerCarts([]);
      setLoadedAvailableCarts(true);
      return;
    }

    const filteredCarts = powerCartRes.data.filter(
      cart =>
        cart.status === "available" ||
        (cart.status === "occupied" &&
          currentParticipants.some(participant => participant.power_cart_id === cart.id)),
    );

    setAvailablePowerCarts(filteredCarts);
    setLoadedAvailableCarts(true);
  }

  function handleCartIdChange(cartId: number, participantId: number) {
    const seatNumber = selectedPowerCarts.filter(powerCart => powerCart.power_cart_id === cartId).length + 1;

    const powerCartList = !selectedPowerCarts.some(cart => cart.participant_id === participantId)
      ? [
          ...selectedPowerCarts,
          {
            power_cart_id: cartId,
            participant_id: participantId,
            seat_number: seatNumber,
            power_cart_number: null,
          },
        ]
      : selectedPowerCarts.map(powerCart =>
          participantId === powerCart.participant_id
            ? { ...powerCart, power_cart_id: cartId, seat_number: seatNumber, power_cart_number: null }
            : powerCart,
        );

    setSelectedPowerCarts(powerCartList);
  }

  function handleCartNumberChange(cartNumber: number, participantId: number) {
    const seatNumber =
      selectedPowerCarts.filter(powerCart => powerCart.power_cart_number === cartNumber).length + 1;

    const powerCartList = !selectedPowerCarts.some(cart => cart.participant_id === participantId)
      ? [
          ...selectedPowerCarts,
          {
            power_cart_id: null,
            participant_id: participantId,
            seat_number: seatNumber,
            power_cart_number: cartNumber,
          },
        ]
      : selectedPowerCarts.map(powerCart =>
          participantId === powerCart.participant_id
            ? { ...powerCart, power_cart_id: null, seat_number: seatNumber, power_cart_number: cartNumber }
            : powerCart,
        );

    setSelectedPowerCarts(powerCartList);
  }

  async function saveCartNumbers() {
    const filtered = selectedPowerCarts.filter(
      cart =>
        (cart.power_cart_id !== undefined || cart.power_cart_number !== undefined) && cart.seat_number !== undefined,
    );

    const response = await Promise.all(
      filtered.map(cart =>
        PutBookingParticipant(
          {
            id: cart.participant_id,
            power_cart_id: cart.power_cart_id ?? undefined,
            seat_number: cart.seat_number ?? undefined,
            power_cart_number: cart.power_cart_number ?? undefined,
          },
          true,
        ),
      ),
    );

    if (response.some(res => res.status !== StatusCode.OK)) {
      dispatch(showError(t("Unable to save cart number")));
      return;
    }

    onSaveSuccess?.();
    onCancel();
  }

  function handleClose() {
    setSelectedPowerCarts(
      participants.map(participant => ({
        power_cart_id: participant.power_cart_id ?? null,
        participant_id: participant.participant_id,
        seat_number: participant.seat_number ?? null,
        power_cart_number: participant.power_cart_number ?? null,
      })),
    );
    onCancel();
  }

  return (
    <Portal isMounted={open}>
      <Sheet
        size="medium"
        height="flexible"
        closable
        title={t("Power Cart Numbers")}
        cancelText={t("Cancel")}
        open={open}
        okText={t("Save")}
        overflow
        onOk={() => {
          void saveCartNumbers();
        }}
        onOKWhenEnterPressed
        onCancel={handleClose}
      >
        <div>
          {participants.map((participant, index) => (
            <div className="mb-2" key={participant.participant_id ?? index}>
              <FormLayout>
                <FormLayout.Group>
                  <div>
                    <div className="text-xl text-semibold mr-1">{participant.full_name}</div>
                    <div className="text-sm text-subdued text-semibold">
                      <span className="mr-2">
                        <span className="text-semibold">
                          {participant.guest ? (
                            <span>{t("secure.facility.tee_sheet.tee_sheet_booking_display.015")}</span>
                          ) : null}
                        </span>
                        <span>
                          {!participant.guest ? (
                            <>
                              <span>{participant.customer_type}</span>
                              {participant.cart_customer_type && (
                                <span>{`(${String(participant.cart_customer_type)})`}</span>
                              )}
                            </>
                          ) : null}
                        </span>
                      </span>
                    </div>
                  </div>

                  {facilityStore.teeSheetSettings?.use_cart_inventory ? (
                    <SelectNew
                      label={t("Power Cart")}
                      id={`power-cart-select-${participant.participant_id}`}
                      options={availablePowerCarts
                        ?.filter(availableCart => {
                          return (
                            selectedPowerCarts.filter(
                              selectedCarts => selectedCarts.power_cart_id === availableCart.id,
                            ).length < MAX_SEATS_PER_CART ||
                            selectedPowerCarts.some(
                              selectedCarts =>
                                selectedCarts.power_cart_id === availableCart.id &&
                                selectedCarts.participant_id === participant.participant_id,
                            )
                          );
                        })
                        .map(cart => ({
                          label: cart.name,
                          id: cart.id,
                        }))}
                      onChange={(value: number) => handleCartIdChange(value, participant.participant_id)}
                      value={
                        (selectedPowerCarts.find(cart => cart.participant_id === participant.participant_id)
                          ?.power_cart_id ?? "") as number
                      }
                      disabled={!loadedAvailableCarts}
                      searchable={0.5}
                      usePortal
                    />
                  ) : (
                    <Input
                      label={t("Cart Number")}
                      value={
                        selectedPowerCarts.find(cart => cart.participant_id === participant.participant_id)
                          ?.power_cart_number ?? ""
                      }
                      type={"number"}
                      onChange={e =>
                        handleCartNumberChange(Number(e.target.value), participant.participant_id)
                      }
                    />
                  )}

                  <Input
                    label={t("Seat Number")}
                    value={
                      selectedPowerCarts.find(cart => cart.participant_id === participant.participant_id)
                        ?.seat_number ?? ""
                    }
                    readOnly
                  />
                </FormLayout.Group>
              </FormLayout>
            </div>
          ))}
        </div>
      </Sheet>
    </Portal>
  );
}
